import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { Header } from "components/DesignSystem/Header/Header";
import { Form1099VendorOptions } from "components/Form1099Export/Form1099VendorOptions";
import { Sync } from "components/icons/Sync";
import { LockedUi } from "components/LockedUi/LockedUi";
import { AddVendor } from "components/Vendors/AddVendor/AddVendor";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  useLazyGetVendorsQuery,
  VendorRequest,
  vendorsApi,
} from "store/apis/vendors";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";

type VendorHeaderProps = {
  entityId: string;
  groupId: string;
  vendorQuery: VendorRequest;
};

export const VendorHeader = ({
  vendorQuery,
  entityId,
  groupId,
}: VendorHeaderProps) => {
  const [getVendors, { isFetching }] = useLazyGetVendorsQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const { alertToast, successToast } = useToast();
  const dispatch = useAppDispatch();
  const { isAdmin } = useRoleBasedView();

  const refreshMerchants = async () => {
    try {
      const data = await getVendors(vendorQuery).unwrap();
      successToast({
        message: "Vendors refreshed successfully",
      });

      dispatch(
        vendorsApi.util.updateQueryData(
          "getVendors",
          { ...vendorQuery, refresh_merchants: false },
          (draft) => {
            return data;
          }
        )
      );
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const exportVendors = async () => {
    try {
      const { document_id } = await getVendors({
        ...vendorQuery,
        generate_report: true,
      }).unwrap();
      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();
      openLink(download_url, "_blank");
      successToast({ message: "Vendor export successful" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  return (
    <Header
      v2
      title="Vendors"
      right={
        <div className="t-flex t-gap-2 t-ml-auto">
          <Button
            size="xsmall"
            customType="icon"
            isLoading={isFetching}
            disabled={isFetching}
            onClick={refreshMerchants}
          >
            <div className="t-text-text-30">
              <Sync />
            </div>
          </Button>
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.EXPENSE}
            blockedUi={<LockedButton text="Add vendors" />}
          >
            <AddVendor entityId={entityId} />
          </LockedUi>
          {isAdmin && <Form1099VendorOptions exportVendors={exportVendors} />}
        </div>
      }
    />
  );
};
